<template>
    <div>
        <DocumentDetailsModal
            v-if="documents.length > 0"
            :email="email"
            :documents="documents"
            :requirement-label="computedReqTypeLabel"
            :uploaded-at="submissionDate"
            @close="close"
        />
        <LoadingModal
            v-else-if="isLoading"
            additional-class-name="EntitiesDocumentsModal EntitiesDocumentsModal--loading"
            header="Documents"
            full-screen
            @close="close"
        />
        <EntitiesDocumentsStatusModal
            v-else-if="status === 'error' || documents.length === 0"
            :type="statusModalType"
            :message="statusModalMessage"
            @close="close"
        />
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import startCase from 'lodash/startCase';

    import {
        VerificationDocument,
    } from '@evidentid/gateway-api-client/src/models/submission-tprm/VerificationDocument.model';
    import { TprmRequirementDefinition } from '@evidentid/tprm-portal-lib/models/common';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
    import {
        VerificationRequest,
    } from '@evidentid/tprm-portal-lib/models/notification-configuration/VerificationRequest.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import LoadingModal from '@/components/loading-modal/LoadingModal.vue';
    import { useEntityDetailsStore } from '@/modules/entity-details/store';
    import DocumentDetailsModal from '@/modules/entity-documents/components/DocumentDetailsModal.vue';
    import EntitiesDocumentsStatusModal from '@/modules/entity-documents/components/EntitiesDocumentsStatusModal.vue';
    import { EntityDocumentsState, useEntityDocumentsStore } from '@/modules/entity-documents/store';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    @Component({
        emits: [ 'abort' ],
        components: {
            DocumentDetailsModal,
            LoadingModal,
            EntitiesDocumentsStatusModal,
        },
    })
    export default class EntityCoverageDocumentProcedure extends Vue {
        @Prop({ type: String })
        private rpName!: string;

        @Prop({ type: String })
        private email!: string;

        @Prop({ type: String })
        private requirementType!: TprmRequirementType;

        @Prop({ type: String })
        private requirementInstanceLabel?: string;

        @Prop({ type: String })
        private requirementInstanceId?: string | null;

        @Prop({ type: String })
        private entityId?: string;

        @Prop({ type: String, required: true })
        private verificationId!: string;

        @Prop({ type: String, default: null })
        private customNoDocMsg!: string | null;

        // Set when a document was expected, so customNoDocMsg describes an empty lookup.
        @Prop({ type: Boolean, default: false })
        private documentExpected!: boolean;

        private submissionDate: string | null = null;

        private get state(): EntityDocumentsState {
            return useEntityDocumentsStore().$state;
        }

        private get documents(): VerificationDocument[] {
            const rpDocs = this.state.rprDocuments[this.rpName] || {};
            const emailDocs = rpDocs[this.email] || {};
            return emailDocs.content || [];
        }

        private get status(): OperationStatus {
            const rpDocs = this.state.rprDocuments[this.rpName] || {};
            const emailDocs = rpDocs[this.email] || {};
            return emailDocs.status || OperationStatus.uninitialized;
        }

        private get isLoadingVerification(): boolean {
            return this.verificationsStatus.status === OperationStatus.loading;
        }

        private get verificationsStatus(): { status: OperationStatus, list: VerificationRequest[] } {
            return useEntityDetailsStore().verificationRequestsStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get isLoading(): boolean {
            return this.isLoadingVerification || this.status === 'loading';
        }

        private get needsVerification(): boolean {
            return Boolean(this.entityId);
        }

        private get computedReqTypeLabel(): string {
            const combinatorPattern = /[_-]/g;
            const whiteSpace = ' ';
            const typeLabel: string = this.requirementTypeLabel ||
                startCase(this.requirementType.replace(combinatorPattern, whiteSpace).toLowerCase());
            return this.requirementInstanceLabel ? `${typeLabel} - ${this.requirementInstanceLabel}` : typeLabel;
        }

        private get requirementTypeLabel(): string {
            return this.requirementTypeDefinition?.defaultLabel || startCase(this.requirementType);
        }

        private get requirementTypeDefinition(): TprmRequirementDefinition | null {
            return this.requirementModels[this.requirementType] || null;
        }

        private get requirementModels(): Record<string, TprmRequirementDefinition> {
            return useDashboardStore().tprmRequirementsDefinitions || {};
        }

        private get statusModalMessage(): string {
            if (this.status === OperationStatus.error) {
                return 'Something went wrong, please try again.';
            }
            // The lookup never ran (for example, the verification isn't in the entity's list), so the
            // empty-lookup message doesn't apply yet.
            if (this.documentExpected && this.status !== OperationStatus.success) {
                return 'Document Not Available';
            }
            return this.customNoDocMsg || 'Document Not Available';
        }

        private get statusModalType(): 'error' | 'warning' {
            if (this.status === OperationStatus.error) {
                return 'error';
            }
            return 'warning';
        }

        @Watch('status', { immediate: true })
        private onLoadingStatusChange(): void {
            this.conditionallyLoadDocument();
        }

        @Watch('isLoadingVerification', { immediate: true })
        private onLoadingVerificationChange(): void {
            this.conditionallyLoadDocument();
        }

        private conditionallyLoadDocument(): void {
            const docNotLoaded = this.status === OperationStatus.uninitialized;
            const hasVerification = this.verificationsStatus.status === OperationStatus.success;
            if (this.needsVerification && this.verificationsStatus.status === OperationStatus.uninitialized) {
                useEntityDetailsStore().loadVerificationRequests({
                    rpName: this.rpName,
                    entityId: this.entityId as string,
                });
            }
            if ((this.needsVerification && hasVerification && docNotLoaded) ||
                (!this.needsVerification && docNotLoaded)) {
                this.loadDocument();
            }
        }

        private loadDocument(): void {
            const verification = this.verificationsStatus.list
                .find(({ verificationId }) => verificationId === this.verificationId);
            if (!verification) {
                console.warn(`Verification ${this.verificationId} not found in the list of verifications`);
                return;
            }
            this.submissionDate = verification.inputReceivedAt || null;

            useEntityDocumentsStore().loadRprDocumentDetails({
                rpName: this.rpName,
                email: this.email,
                verificationId: this.verificationId,
                requirement: this.requirementType,
                instanceId: this.requirementInstanceId,
            });
        }

        private unmounted() {
            // TODO: Handle race condition: closing modal during loading will cause data being in memory
            useEntityDocumentsStore().clearRprDocumentDetails({
                rpName: this.rpName,
                email: this.email,
            });
            if (this.needsVerification && this.verificationsStatus.status !== OperationStatus.uninitialized) {
                // only clear the verification if there was request from this procedure
                useEntityDetailsStore().clearVerificationRequests({
                    rpName: this.rpName,
                });
            }
        }

        private close(): void {
            this.$emit('abort');
        }
    }
</script>
